import {
  DataTable,
  Notice,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { WIDGET_H3 } from "@/components/tool-kit/styles";
import { BitGrid } from "@/components/tools/cs-tools/bit-grid";
import type { HAMMING_CODE_CALCULATOR_STRINGS } from "@/components/tools/hamming-code-calculator";
import { t } from "@/lib/i18n/interpolate";
import {
  type CheckOutcome,
  type EncodeOutcome,
  type Layout,
  roleLabels,
  syndromeTable,
  type Verdict,
} from "@/lib/tool-kit/cs-tools/hamming";

export type Strings = typeof HAMMING_CODE_CALCULATOR_STRINGS;
export type Fmt = (value: number, options?: Intl.NumberFormatOptions) => string;

const PLAIN: Intl.NumberFormatOptions = {
  useGrouping: false,
  maximumFractionDigits: 0,
};
const SYNDROME_ROWS = 16;

function num(fmt: Fmt, value: number): string {
  return fmt(value, PLAIN);
}

function positionsOf(layout: Layout, extended: boolean): number[] {
  const first = extended ? 0 : 1;
  return Array.from({ length: layout.n + 1 - first }, (_, i) => first + i);
}

function roleText(role: string, strings: Strings): string {
  if (role === "P0") {
    return strings.roleOverall;
  }
  return role;
}

/** The sent / received / corrected words laid out one bit per column, by position. */
export function WordGrid({
  layout,
  extended,
  rows,
  strings,
  fmt,
}: {
  layout: Layout;
  extended: boolean;
  rows: { label: string; word: string; strong?: boolean }[];
  strings: Strings;
  fmt: Fmt;
}) {
  const roles = roleLabels(layout, extended).map((r) => roleText(r, strings));
  return (
    <BitGrid
      caption={strings.gridCaption}
      corner={strings.gridCorner}
      head={positionsOf(layout, extended).map((p) => num(fmt, p))}
      rows={[
        { label: strings.rowRole, cells: roles },
        ...rows.map((row) => ({
          label: row.label,
          cells: row.word.split(""),
          strong: row.strong,
        })),
      ]}
    />
  );
}

export function SyndromeTable({
  layout,
  extended,
  strings,
  fmt,
}: {
  layout: Layout;
  extended: boolean;
  strings: Strings;
  fmt: Fmt;
}) {
  const rows = syndromeTable(layout, SYNDROME_ROWS);
  const total = 2 ** layout.r;
  const roles = roleLabels(layout, extended);
  const offset = extended ? 0 : 1;
  return (
    <section className="space-y-3">
      <h3 className={WIDGET_H3}>{strings.syndromeHeading}</h3>
      <p className="text-muted-foreground text-sm">{strings.syndromeBody}</p>
      <DataTable
        caption={strings.syndromeCaption}
        head={[
          strings.colSyndromeBinary,
          strings.colSyndromeValue,
          strings.colMeaning,
        ]}
        rows={rows.map((row) => {
          let meaning: string = strings.syndromeNone;
          if (!row.valid) {
            meaning = t(strings.syndromeImpossible, { n: num(fmt, layout.n) });
          } else if (row.position !== null) {
            meaning = t(strings.syndromeAt, {
              position: num(fmt, row.position),
              role: roles[row.position - offset] ?? "",
            });
          }
          return [
            row.syndrome.toString(2).padStart(layout.r, "0"),
            num(fmt, row.syndrome),
            meaning,
          ];
        })}
      />
      {total > rows.length ? (
        <p className="text-muted-foreground text-sm">
          {t(strings.syndromeLimited, {
            shown: num(fmt, rows.length),
            total: num(fmt, total),
          })}
        </p>
      ) : null}
    </section>
  );
}

function ParityTable({
  encoded,
  strings,
  fmt,
}: {
  encoded: EncodeOutcome;
  strings: Strings;
  fmt: Fmt;
}) {
  const parityWord =
    encoded.options.parity === "odd"
      ? strings.parityOddWord
      : strings.parityEvenWord;
  return (
    <section className="space-y-3">
      <h3 className={WIDGET_H3}>{strings.parityHeading}</h3>
      <p className="text-muted-foreground text-sm">
        {t(strings.parityBody, { parity: parityWord })}
      </p>
      <DataTable
        caption={strings.parityCaption}
        head={[
          strings.colCheck,
          strings.colCovers,
          strings.colDataBits,
          strings.colOnes,
          strings.colParityBit,
        ]}
        rows={encoded.rows.map((row) => [
          `P${num(fmt, row.position)}`,
          row.covers.map((p) => num(fmt, p)).join(", "),
          row.dataBits,
          num(fmt, row.ones),
          String(row.value),
        ])}
      />
      {encoded.overall === null ? null : (
        <p className="text-muted-foreground text-sm">
          {t(strings.overallNote, { bit: String(encoded.overall) })}
        </p>
      )}
    </section>
  );
}

export function EncodeSection({
  encoded,
  strings,
  fmt,
}: {
  encoded: EncodeOutcome;
  strings: Strings;
  fmt: Fmt;
}) {
  const { layout, options } = encoded;
  const extended = options.extended;
  const total = layout.n + (extended ? 1 : 0);
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.statCodeword}
          value={encoded.codeword}
        />
        <StatCard
          label={strings.statCode}
          value={t(strings.codeName, {
            n: num(fmt, total),
            k: num(fmt, layout.k),
          })}
        />
        <StatCard
          label={strings.statRate}
          value={`${fmt(layout.k / total, { maximumFractionDigits: 3 })}`}
        />
      </StatGrid>
      <WordGrid
        extended={extended}
        fmt={fmt}
        layout={layout}
        rows={[
          { label: strings.rowSent, word: encoded.codeword, strong: true },
        ]}
        strings={strings}
      />
      <ParityTable encoded={encoded} fmt={fmt} strings={strings} />
    </>
  );
}

function verdictText(check: CheckOutcome, strings: Strings, fmt: Fmt): string {
  const verdict: Verdict = check.verdict;
  const roles = roleLabels(check.layout, check.options.extended);
  const offset = check.options.extended ? 0 : 1;
  switch (verdict) {
    case "none":
      return strings.verdictNone;
    case "overallBit":
      return strings.verdictOverall;
    case "doubleError":
      return t(strings.verdictDouble, { syndrome: num(fmt, check.syndrome) });
    case "invalidSyndrome":
      return t(strings.verdictInvalid, {
        syndrome: num(fmt, check.syndrome),
        n: num(fmt, check.layout.n),
      });
    default: {
      const position = check.errorPosition ?? 0;
      return t(strings.verdictCorrected, {
        position: num(fmt, position),
        role: roles[position - offset] ?? "",
      });
    }
  }
}

export function CheckSection({
  check,
  sent,
  strings,
  fmt,
}: {
  check: CheckOutcome;
  /** The word that was sent, when known: lets the widget say if the repair is wrong. */
  sent: string | null;
  strings: Strings;
  fmt: Fmt;
}) {
  const extended = check.options.extended;
  const wrong =
    sent !== null &&
    check.verdict !== "doubleError" &&
    check.verdict !== "invalidSyndrome" &&
    check.corrected !== sent;
  const parityWord =
    check.options.parity === "odd"
      ? strings.parityOddWord
      : strings.parityEvenWord;
  return (
    <>
      <Notice>{verdictText(check, strings, fmt)}</Notice>
      {wrong ? (
        <p className="text-muted-foreground text-sm" role="alert">
          {strings.miscorrectNote}
        </p>
      ) : null}
      <StatGrid>
        <StatCard
          emphasis
          label={strings.statSyndrome}
          value={`${check.syndrome.toString(2).padStart(check.layout.r, "0")} (${num(fmt, check.syndrome)})`}
        />
        <StatCard label={strings.statCorrected} value={check.corrected} />
        <StatCard label={strings.statData} value={check.data} />
      </StatGrid>
      <WordGrid
        extended={extended}
        fmt={fmt}
        layout={check.layout}
        rows={[
          { label: strings.rowReceived, word: check.received },
          { label: strings.rowCorrected, word: check.corrected, strong: true },
        ]}
        strings={strings}
      />
      <section className="space-y-3">
        <h3 className={WIDGET_H3}>{strings.checksHeading}</h3>
        <p className="text-muted-foreground text-sm">
          {t(strings.checksBody, { parity: parityWord })}
        </p>
        <DataTable
          caption={strings.checksCaption}
          head={[
            strings.colCheck,
            strings.colCovers,
            strings.colReceivedBits,
            strings.colOnes,
            strings.colResult,
          ]}
          rows={check.rows.map((row) => [
            `P${num(fmt, row.position)}`,
            row.covers.map((p) => num(fmt, p)).join(", "),
            row.bits,
            num(fmt, row.ones),
            row.ok ? strings.resultPass : strings.resultFail,
          ])}
        />
        {check.overallOk === null ? null : (
          <p className="text-muted-foreground text-sm">
            {check.overallOk ? strings.overallHolds : strings.overallFails}
          </p>
        )}
      </section>
    </>
  );
}
